<script setup>
import { computed, ref } from 'vue'
import {usePageNumberStore} from '@/services/store.js'
import { useRoute } from 'vue-router'

const pageNumber = usePageNumberStore()
const route = useRoute();
const currentPage = ref(1);

const props = defineProps({
  totalPages: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['page-changed'])

const pages = computed(() => {
  currentPage.value = pageNumber.getPage(route.name)
  const currPage = Math.max(1, Math.min(currentPage.value, props.totalPages))
  const totalPages = Math.max(1, props.totalPages)

  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  let result = [1]

  if (currPage > 4) {
    result.push({ label: '...', clickable: true, page: Math.floor(currPage / 2), title: 'Vai alla pagina ' + Math.floor(currPage / 2) })
  }

  const start = Math.max(2, currPage - 2)
  const end = Math.min(totalPages - 1, currPage + 2)

  for (let i = start; i <= end; i++) {
    result.push(i)
  }

  if (currPage < totalPages - 3) {
    result.push({ label: '...', clickable: true, page: Math.floor((currPage + totalPages) / 2), title: 'Vai alla pagina ' + Math.floor((currPage + totalPages) / 2) })
  }

  if (totalPages > 1) {
    result.push(totalPages)
  }

  return result
})

const changePage = (page) => {
  if (page >= 1 && page <= props.totalPages) {
    pageNumber.setPageNumber(page, route.name)
    emit('page-changed', page)
  }
  currentPage.value = pageNumber.getPage(route.name)
}
</script>

<template>
  <nav aria-label="Page navigation">
    <ul class="pagination justify-content-end mb-0 mt-3">
      <li class="page-item" :class="{ disabled: currentPage === 1 }">
        <a class="page-link" href="#" @click.prevent="changePage(1)" title="Vai alla prima pagina">&laquo;</a>
      </li>
      <li class="page-item" :class="{ disabled: currentPage === 1 }">
        <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">&lt;</a>
      </li>
      <li
        v-for="page in pages"
        :key="typeof page === 'object' ? page.label : page"
        class="page-item"
        :class="{ active: page === currentPage }"
      >
        <a
          v-if="typeof page === 'object'"
          :title="page.title"
          class="page-link"
          href="#"
          @click.prevent="changePage(page.page)"
        >{{ page.label }}</a
        >
        <a v-else
           class="page-link"
           href="#"
           @click.prevent="page !== currentPage ? changePage(page) : null"
           :class="{ disabled: page === currentPage }"
        >{{ page }}</a>
      </li>
      <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
        <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">&gt;</a>
      </li>
      <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
        <a class="page-link" href="#" @click.prevent="changePage(totalPages)" title="Vai all'ultima pagina"
        >&raquo;</a
        >
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.page-item.disabled .page-link {
  pointer-events: none;
  cursor: default;
  opacity: 0.5;
}

.page-item:first-of-type .page-link {
  border-top-left-radius: var(--bs-pagination-border-radius) !important;
  border-bottom-left-radius: var(--bs-pagination-border-radius) !important;
}

.page-item .page-link {
  border-radius: 0 !important;
}

.page-item:last-of-type .page-link {
  border-top-right-radius: var(--bs-pagination-border-radius) !important;
  border-bottom-right-radius: var(--bs-pagination-border-radius) !important;
}
</style>
